import { useNotifications, NotificationType } from 'modules/app/notifications';
import { useCostCentersQuery } from 'modules/budgets/apis';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { type SyncMode } from 'src/core/modules/accounting-integration/apis/useSetSyncSettings';
import { type SaveChanges } from 'src/core/modules/accounting-integration/hooks/useAnalyticalFieldMapping';
import { type AnalyticalFieldMappingV2 } from 'src/core/modules/bookkeep';

import { useCostCenterReplacePreviewQuery } from './hooks/useCostCenterReplacePreviewQuery';
import { useDeleteCostCenterQuery } from './hooks/useDeleteCostCenterQuery';
import { useReplaceCostCenterQuery } from './hooks/useReplaceCostCenterQuery';
import { CostCenterDeletionModal } from '../../components/CostCenterDeletionModal/CostCenterDeletionModal';
import type { CostCenterWithBudgets } from '../../costCenter';

type Props = {
  costCenter: CostCenterWithBudgets;
  isOpen: boolean;
  onCloseModal(): void;
  onCancel(): void;
  saveMappingChanges(mappingChanges: SaveChanges): void;
  isMapSyncEnabled?: boolean;
  fieldMapping?: AnalyticalFieldMappingV2;
  syncMode?: SyncMode;
};

export const CostCenterDeletionModalContainer = ({
  costCenter,
  isOpen,
  onCloseModal,
  onCancel,
  saveMappingChanges,
  isMapSyncEnabled,
  fieldMapping,
  syncMode,
}: Props) => {
  const { pushNotif } = useNotifications();
  const { t } = useTranslation('global');
  const costCenterReplacePreviewQueryState = useCostCenterReplacePreviewQuery(
    costCenter.id,
  );
  const costCentersQueryState = useCostCentersQuery();
  const [replaceCostCenter] = useReplaceCostCenterQuery(costCenter.id);
  const [deleteCostCenter] = useDeleteCostCenterQuery(costCenter.id);

  if (
    costCentersQueryState.status === 'loading' ||
    costCenterReplacePreviewQueryState.status === 'loading'
  ) {
    // TODO: add loader
    return null;
  }

  if (
    costCentersQueryState.status === 'error' ||
    costCenterReplacePreviewQueryState.status === 'error'
  ) {
    pushNotif({
      type: NotificationType.Danger,
      message: t('costCenters.deletion.loadFailure'),
    });
    return null;
  }

  const availableSubstituteCostCenters = costCentersQueryState.data.filter(
    (cc) => cc.id !== costCenter.id,
  );

  const handleComplete = async (costCenterId: string | undefined) => {
    try {
      if (costCenterId) {
        await replaceCostCenter({ replacementId: costCenterId });
      } else {
        await deleteCostCenter();

        if (isMapSyncEnabled && fieldMapping?.internalId) {
          saveMappingChanges({
            mapping: {
              fieldId: fieldMapping.id,
              fieldType: fieldMapping.fieldType,
              internalId: fieldMapping.internalId,
              values: fieldMapping.values.map((value) => {
                if (value.internalId === costCenter.id) {
                  return {
                    ...value,
                    internalId: null,
                  };
                }
                return value;
              }),
            },
          });
        }
      }
      pushNotif({
        type: NotificationType.Success,
        message: t('costCenters.deletion.notifSuccess', {
          name: costCenter.name,
        }),
      });
      onCloseModal();
    } catch {
      pushNotif({
        type: NotificationType.Danger,
        message: t('costCenters.deletion.notifFailure', {
          name: costCenter.name,
        }),
      });
    }
  };

  const isCostCenterMapped =
    syncMode === 'pullAndMap' &&
    fieldMapping?.values.some((value) => value.internalId === costCenter.id);

  return (
    <CostCenterDeletionModal
      isOpen={isOpen}
      costCenter={{
        id: costCenter.id,
        name: costCenter.name,
        hasBudgets: costCenter.hasBudgets,
        replacePreview: costCenterReplacePreviewQueryState.data,
      }}
      substituteCostCenters={availableSubstituteCostCenters}
      onComplete={handleComplete}
      onCancel={onCancel}
      isCostCenterMapped={isCostCenterMapped}
    />
  );
};
